import { isDefined } from "@clipboard-health/util-ts";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { minutesToMilliseconds } from "date-fns";

import { useFetchPaginatedPlacements } from "../../Placements/api/useFetchPaginatedPlacements";
import { useGetMultipleChoiceAggregates } from "../Review/api/useGetMultipleChoiceAggregates";
import { type QuestionTag } from "../Review/types";
import { useGetWorkplacesById } from "../useGetWorkplacesById";
import {
  type AggregatedReviews,
  buildWorkplaceReviewHighlights,
} from "./buildWorkplaceReviewHighlights";
import { WorkplaceHighlightType } from "./constants";
import type { WorkplaceHighlight } from "./types";

interface UseWorkplaceHighlightsProps {
  workplaceId?: string;
  excludeList?: QuestionTag[];
  /**
   * When true, patient ratio highlights are shown as pills.
   * When false or undefined, they are shown in the Patient Load section instead.
   * Set to false when useDelayedReviewUI flag is enabled.
   */
  includePatientRatioHighlights?: boolean;
}

interface UseWorkplaceHighlightsResult {
  highlights: WorkplaceHighlight[];
  isLoading: boolean;
}

export function useWorkplaceHighlights(
  props: UseWorkplaceHighlightsProps
): UseWorkplaceHighlightsResult {
  const { workplaceId, excludeList, includePatientRatioHighlights = true } = props;

  // Fetch review highlights data
  const { data: multipleChoiceAggregates = [], isLoading: reviewHighlightsLoading } =
    useGetMultipleChoiceAggregates(workplaceId ?? "", {
      enabled: isDefined(workplaceId),
      staleTime: minutesToMilliseconds(5),
      useErrorBoundary: false,
    });

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const { data: workplaces } = useGetWorkplacesById(
    { workplaceIds: workplaceId ? [workplaceId] : [] },
    { enabled: isDefined(workplaceId) }
  );
  const isIndependentContractorBadgesRequired =
    workplaces?.pages.at(0)?.data.at(0)?.attributes.independentContractorBadge.enabled ?? false;

  const qualifications =
    workerQualificationsAreLoaded && workerQualifications
      ? (workerQualifications?.map((q) => q.attributes.name) ?? [])
      : [];

  const aggregatedReviews: AggregatedReviews[] = multipleChoiceAggregates.map((aggregate) => ({
    tags: aggregate.attributes.question.tags.map(({ tag }) => tag),
    text: aggregate.attributes.text,
    variations: aggregate.attributes.variations as AggregatedReviews["variations"],
  }));

  const reviewHighlights = buildWorkplaceReviewHighlights({
    aggregatedReviews,
    qualifications,
    excludeList,
    mandatoryBreakDurationInMinutes,
    includePatientRatioHighlights,
  });

  const { userId: workerId } = useDefinedWorker();
  const {
    data: placementsData,
    isSuccess: isPlacementsSuccess,
    isLoading: isPlacementsLoading,
  } = useFetchPaginatedPlacements(
    {
      workerId,
      filter: { workplaceId },
      placementCandidateId: "",
      shouldLogEvent: false,
    },
    {
      enabled: isDefined(workerId) && isDefined(workplaceId),
    }
  );

  const hasPlacements =
    isPlacementsSuccess && placementsData?.pages.some((page) => page.data.length > 0);
  const placementHighlights: WorkplaceHighlight[] = hasPlacements
    ? [
        {
          type: WorkplaceHighlightType.HIRING,
          label: "This workplace is hiring!",
        },
      ]
    : [];

  const highlights = [...placementHighlights, ...reviewHighlights];

  if (isIndependentContractorBadgesRequired) {
    highlights.push({
      type: WorkplaceHighlightType.INDEPENDENT_CONTRACTOR_BADGE_REQUIRED,
      label: "Badge required",
    });
  }

  return {
    isLoading: reviewHighlightsLoading || isPlacementsLoading,
    highlights,
  };
}
